import {FC} from 'react'
import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {useDeleteUser} from '@/features/users/queries/useDeleteUser.ts'
import {errorHandler} from '@utilities/helpers.ts'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient.ts'
import {UserBase} from '@/features/users/types.ts'
import {toast} from '@utilities/toast/toast.tsx'
import {DeleteModal} from '@/components/commons/delete-modal/DeleteModal'
import {generatePath, useNavigate} from 'react-router-dom'
import {routes} from '@/utilities/constants/routes'
import {usePermissions} from '@/features/auth/store/store'

interface DeleteUserModalProps {
    onClose: () => void
    onSuccess?: () => void
    user: UserBase
    // Where to land after a successful delete; defaults to the users list the current role can see
    redirectTo?: string
}
export const DeleteUserModal: FC<DeleteUserModalProps> = ({user, onClose, onSuccess, redirectTo}) => {
    const {t} = useTranslation()
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const permissions = usePermissions()
    const defaultRedirectTo = permissions?.users?.write
        ? routes.USERS_ROUTE.handle.path
        : routes.OPERATIONAL_USERS_ROUTE.handle.path

    const deleteMutation = useDeleteUser(user.id, {
        onError: errorHandler,
        onSuccess: async () => {
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.USERS]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.USERS]})
            toast.success(t('commons:delete_success', {fullName: user.fullName}))
            navigate(redirectTo ?? generatePath(defaultRedirectTo))
            onSuccess?.()
            onClose()
        }
    })

    return (
        <DeleteModal
            onClose={onClose}
            deleteMutation={deleteMutation}
            canBeDeleted={user.canBeDeleted}
            fullName={user.fullName}
            paragraph1={t('commons:delete_paragraph_1')}
            title={t('users:delete_user_title')}
            deniedTitle={t('users:cannot_delete_user_title')}
            deniedParagraph={
                <Trans
                    i18nKey="commons:cannot_delete_paragraph"
                    values={{fullName: user.fullName}}
                    components={{b: <b />}}
                />
            }
        />
    )
}
